/* iter-50c — Author Funds view.
 *
 * Aggregates every project wallet owned by the current user. Surfaces
 * total tokens, per-project breakdown, monthly cap, and the last 15
 * cost events so the author can answer "how much do I have left for
 * writing services?" at a glance.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Coins, ShieldCheck, Loader2, ArrowLeft, PlusCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtTokens(n) { return (Number(n) || 0).toLocaleString(); }

function fmtTs(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function FundsPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me/wallet-summary`, { withCredentials: true, timeout: 15000 });
        setData(r.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || 'Could not load wallet');
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground scroll-thin" data-testid="funds-page">
      <div className="sticky top-0 z-20 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <button
            type="button"
            onClick={() => nav('/app')}
            data-testid="funds-back-btn"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> The Loom
          </button>
          <h1 className="text-[15px] font-semibold tracking-tight">My Funds</h1>
          <span className="text-[11px] text-muted-foreground">— writing-services balance</span>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-5 px-4 py-6 sm:px-6">
        {busy ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center text-destructive">
            Could not load wallet summary. Refresh or contact support.
          </div>
        ) : (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="funds-totals">
              <SummaryCard
                icon={<Coins className="h-4 w-4" />}
                label="AI tokens"
                value={fmtTokens(data.totals.tokens_purchased)}
                hint="Used for Claude story generation"
              />
              <SummaryCard
                icon={<ShieldCheck className="h-4 w-4" />}
                label="Plagiarism credits"
                value={fmtTokens(data.totals.copyscape_tokens)}
                hint="Copyscape checks · 1 credit per scan"
              />
              <SummaryCard
                icon={<Wallet className="h-4 w-4" />}
                label="Projects"
                value={String(data.totals.project_count)}
                hint="Each project has its own wallet"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Per-project table */}
              <section className="lg:col-span-2 rounded-md border border-border bg-card" data-testid="funds-per-project">
                <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
                  <h2 className="text-[12.5px] font-semibold">Per-project balances</h2>
                  <button
                    type="button"
                    onClick={() => nav('/app')}
                    className="inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline"
                    data-testid="funds-buy-tokens-btn"
                  >
                    <PlusCircle className="h-3 w-3" /> Buy tokens
                  </button>
                </header>
                {data.per_project.length === 0 ? (
                  <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                    Create a project to start using writing services.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                      <thead className="bg-secondary/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left">Project</th>
                          <th className="px-3 py-2 text-right">AI tokens</th>
                          <th className="px-3 py-2 text-right">Plagiarism</th>
                          <th className="px-3 py-2 text-right">Cap (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.per_project.map((row) => (
                          <tr
                            key={row.project_id}
                            data-testid={`funds-project-row-${row.project_id}`}
                            className="cursor-pointer border-t border-border hover:bg-secondary/20"
                            onClick={() => nav(`/app?project=${encodeURIComponent(row.project_id)}`)}
                          >
                            <td className="px-3 py-2.5 font-medium">{row.title}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{fmtTokens(row.tokens_purchased)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{fmtTokens(row.copyscape_tokens)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                              {row.monthly_cap_usd != null ? `$${row.monthly_cap_usd}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Recent events */}
              <section className="rounded-md border border-border bg-card" data-testid="funds-recent">
                <header className="border-b border-border px-4 py-2.5">
                  <h2 className="text-[12.5px] font-semibold">Recent activity</h2>
                </header>
                {data.recent_events.length === 0 ? (
                  <div className="p-6 text-center text-[12px] text-muted-foreground">
                    Spend on AI generation or plagiarism scans will appear here.
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.recent_events.map((ev, i) => (
                      <li key={`${ev.ts}-${i}`} className="px-3 py-2.5 text-[11.5px]" data-testid={`funds-event-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{ev.kind || 'event'}</span>
                          <span className="font-mono text-muted-foreground">
                            {ev.tokens ? `${ev.tokens > 0 ? '+' : ''}${ev.tokens}` : ev.amount_usd != null ? `$${ev.amount_usd}` : ''}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[10.5px] text-muted-foreground">{fmtTs(ev.ts)}</div>
                        {ev.note && <div className="mt-0.5 text-[11px] text-foreground/80">{ev.note}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, hint }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <span className="text-primary">{icon}</span>{label}
      </div>
      <div className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}
